import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../../utils/supabaseUtils';
import './CRMDashboard.css';

// Import CRM components
import ContactsList from './ContactsList';
import LeadManagement from './LeadManagement';
import TeamsList from './TeamsList';
import EventRelations from './EventRelations';
import CommunicationLog from './CommunicationLog';

const CRMDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState({
    contacts: 0,
    teams: 0,
    leads: 0,
    events: 0,
    communications: 0
  });
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccessAndLoadData = async () => {
      try {
        // In a production environment, you would check if the current user has
        // access to the CRM system. For development purposes, we'll assume access is granted.
        setHasAccess(true);
        
        // Load initial data and stats
        await fetchStats();
        
        setLoading(false);
      } catch (error) {
        console.error('Error initializing CRM dashboard:', error);
        setLoading(false);
      }
    };

    checkAccessAndLoadData();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch actual stats from Supabase when tables are available
      // For now, use placeholder data
      setStats({
        contacts: 42,
        teams: 15,
        leads: 23,
        events: 8,
        communications: 156
      });
    } catch (error) {
      console.error('Error fetching CRM stats:', error);
    }
  };

  if (loading) {
    return (
      <div className="crm-loading">
        <div className="spinner"></div>
        <p>Loading CRM dashboard...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You do not have access to the CRM system.</p>
        <Link to="/dashboard" className="back-link">
          Return to Dashboard
        </Link>
      </div>
    );
  }

  // Dashboard overview with stats and quick links
  const renderDashboardOverview = () => {
    return (
      <div className="crm-section">
        <div className="section-header">
          <h3>CRM Dashboard</h3>
          <button 
            className="refresh-button"
            onClick={fetchStats}
            title="Refresh Stats"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
        
        {/* Stats Overview */}
        <div className="crm-stats">
          <div className="stat-card">
            <div className="stat-icon contacts-icon">
              <i className="fas fa-address-book"></i>
            </div>
            <div className="stat-info">
              <h3>CONTACTS</h3>
              <p className="stat-value">{stats.contacts}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon teams-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-info">
              <h3>TEAMS</h3>
              <p className="stat-value">{stats.teams}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon leads-icon">
              <i className="fas fa-funnel-dollar"></i>
            </div>
            <div className="stat-info">
              <h3>LEADS</h3>
              <p className="stat-value">{stats.leads}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon events-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <div className="stat-info">
              <h3>EVENTS</h3>
              <p className="stat-value">{stats.events}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon communications-icon">
              <i className="fas fa-comments"></i>
            </div>
            <div className="stat-info">
              <h3>COMMUNICATIONS</h3>
              <p className="stat-value">{stats.communications}</p>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button onClick={() => setActiveSection('contacts')}>
              <i className="fas fa-user-plus"></i> Add Contact
            </button>
            <button onClick={() => setActiveSection('leads')}>
              <i className="fas fa-plus-circle"></i> Create Lead
            </button>
            <button onClick={() => setActiveSection('teams')}>
              <i className="fas fa-users"></i> Add Team
            </button>
            <button onClick={() => setActiveSection('communications')}>
              <i className="fas fa-comment-medical"></i> Log Communication
            </button>
          </div>
        </div>
        
        {/* Recent Activity */}
        <div className="recent-activity">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon communication">
                <i className="fas fa-phone"></i>
              </div>
              <div className="activity-details">
                <p className="activity-title">Call with Sarah Johnson</p>
                <p className="activity-meta">Yesterday at 2:30 PM by Mark Wilson</p>
                <p className="activity-description">Discussed upcoming tournament in Denver</p>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon lead">
                <i className="fas fa-user-plus"></i>
              </div>
              <div className="activity-details">
                <p className="activity-title">New Lead: Denver Tigers</p>
                <p className="activity-meta">2 days ago by Jennifer Lee</p>
                <p className="activity-description">High school basketball team interested in tournament package</p>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon email">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="activity-details">
                <p className="activity-title">Email to Colorado Rapids</p>
                <p className="activity-meta">3 days ago by Mark Wilson</p>
                <p className="activity-description">Sent follow-up about package options</p>
              </div>
            </div>
          </div>
          
          <button className="view-all-button" onClick={() => setActiveSection('communications')}>
            View All Activity
          </button>
        </div>
        
        {/* Upcoming Follow-ups */}
        <div className="upcoming-followups">
          <h3>Upcoming Follow-ups</h3>
          <div className="followup-list">
            <div className="followup-item">
              <div className="followup-date">
                <p className="date">JUN 15</p>
              </div>
              <div className="followup-details">
                <p className="followup-title">Call with Denver Vikings</p>
                <p className="followup-description">Check if they want to book for the July tournament</p>
              </div>
              <div className="followup-actions">
                <button className="complete-button" title="Mark as complete">
                  <i className="fas fa-check"></i>
                </button>
                <button className="reschedule-button" title="Reschedule">
                  <i className="fas fa-calendar-alt"></i>
                </button>
              </div>
            </div>
            
            <div className="followup-item">
              <div className="followup-date">
                <p className="date">JUN 17</p>
              </div>
              <div className="followup-details">
                <p className="followup-title">Email Boulder Soccer Club</p>
                <p className="followup-description">Send information about team packages</p>
              </div>
              <div className="followup-actions">
                <button className="complete-button" title="Mark as complete">
                  <i className="fas fa-check"></i>
                </button>
                <button className="reschedule-button" title="Reschedule">
                  <i className="fas fa-calendar-alt"></i>
                </button>
              </div>
            </div>
            
            <div className="followup-item">
              <div className="followup-date">
                <p className="date">JUN 21</p>
              </div>
              <div className="followup-details">
                <p className="followup-title">Meeting with Colorado Eagles</p>
                <p className="followup-description">Present proposal for season-long partnership</p>
              </div>
              <div className="followup-actions">
                <button className="complete-button" title="Mark as complete">
                  <i className="fas fa-check"></i>
                </button>
                <button className="reschedule-button" title="Reschedule">
                  <i className="fas fa-calendar-alt"></i>
                </button>
              </div>
            </div>
          </div>
          
          <button className="view-all-button" onClick={() => setActiveSection('leads')}>
            View All Follow-ups
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="crm-dashboard-view">
      <div className="crm-header">
        <h2>PlayMaker Pics CRM</h2>
        <div className="header-actions">
          <Link to="/dashboard" className="dashboard-link" title="Back to Dashboard">
            <i className="fas fa-home"></i>
          </Link>
          <button className="help-button" title="Help & Documentation">
            <i className="fas fa-question-circle"></i>
          </button>
          <button className="settings-button" title="CRM Settings">
            <i className="fas fa-cog"></i>
          </button>
        </div>
      </div>
      
      {/* CRM Navigation */}
      <div className="crm-nav">
        <button 
          className={`nav-button ${activeSection === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveSection('dashboard')}
          data-tooltip="View CRM dashboard and stats"
        >
          <i className="fas fa-tachometer-alt"></i>
          <span>Dashboard</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveSection('contacts')}
          data-tooltip="Manage contacts and individuals"
        >
          <i className="fas fa-address-book"></i>
          <span>Contacts</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveSection('teams')}
          data-tooltip="Manage teams and clubs"
        >
          <i className="fas fa-users"></i>
          <span>Teams</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'leads' ? 'active' : ''}`}
          onClick={() => setActiveSection('leads')}
          data-tooltip="Track and manage sales leads"
        >
          <i className="fas fa-funnel-dollar"></i>
          <span>Leads</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'events' ? 'active' : ''}`}
          onClick={() => setActiveSection('events')}
          data-tooltip="Manage event relationships"
        >
          <i className="fas fa-calendar-alt"></i>
          <span>Events</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'communications' ? 'active' : ''}`}
          onClick={() => setActiveSection('communications')}
          data-tooltip="View communication history"
        >
          <i className="fas fa-comments"></i>
          <span>Communications</span>
        </button>
      </div>
      
      {/* Conditional section rendering */}
      <div className="crm-content">
        {activeSection === 'dashboard' && renderDashboardOverview()}
        {activeSection === 'contacts' && <ContactsList />}
        {activeSection === 'teams' && <TeamsList />}
        {activeSection === 'leads' && <LeadManagement />}
        {activeSection === 'events' && <EventRelations />}
        {activeSection === 'communications' && <CommunicationLog />}
      </div>
    </div>
  );
};

export default CRMDashboard; 